import type {
  EntitlementGrantOutcome,
  EntitlementRepository,
} from "@/features/payment/entitlementRepository";
import type { PurchaseEventRepository } from "@/features/payment/purchaseEventRepository";
import {
  getBundleDefinition,
  getTokenPackDefinition,
} from "@/features/payment/paymentDefinitions";
import type {
  BundleEntitlementKey,
  TokenPackId,
} from "@/features/payment/types";

export const DEV_FREE_PROVIDER_ID = "dev_free";
export const DEV_FREE_GRANT_SOURCE = "dev_free_purchase";

export interface DevFreeGrantDeps {
  readonly ownerId: string;
  readonly entitlementRepository: EntitlementRepository;
  readonly purchaseEventRepository: PurchaseEventRepository;
}

function assertNotProductionBuild(): void {
  if (import.meta.env.PROD === true) {
    throw new Error(
      "dev_free purchase paths are forbidden in production builds",
    );
  }
}

export async function devFreeGrantBundle(
  bundleKey: BundleEntitlementKey,
  deps: DevFreeGrantDeps,
): Promise<EntitlementGrantOutcome> {
  assertNotProductionBuild();
  const definition = getBundleDefinition(bundleKey);
  const outcome = await deps.entitlementRepository.grant({
    ownerId: deps.ownerId,
    entitlementKey: bundleKey,
    grantSource: DEV_FREE_GRANT_SOURCE,
    sourceReference: definition.sku,
  });
  await deps.purchaseEventRepository.record({
    ownerId: deps.ownerId,
    provider: DEV_FREE_PROVIDER_ID,
    productId: bundleKey,
    eventType: outcome === "granted" ? "grant" : "duplicate",
    status: "completed",
    amountMinor: definition.price,
    currencyCode: definition.currency,
    rawPayload: { sku: definition.sku, mode: "dev_free", outcome },
  });
  return outcome;
}

export async function devFreeRecordTokenPack(
  packId: TokenPackId,
  deps: DevFreeGrantDeps,
): Promise<void> {
  assertNotProductionBuild();
  const definition = getTokenPackDefinition(packId);
  await deps.purchaseEventRepository.record({
    ownerId: deps.ownerId,
    provider: DEV_FREE_PROVIDER_ID,
    productId: packId,
    eventType: "consumable_grant",
    status: "completed",
    amountMinor: definition.price,
    currencyCode: definition.currency,
    rawPayload: {
      sku: definition.sku,
      mode: "dev_free",
      tokenGrant: definition.tokenGrant,
    },
  });
}
